import { useEffect, useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { BillToPartnerPicker, type BillToPartnerOption } from "./BillToPartnerPicker";
import {
  formatCentsAsEs,
  MONEY_INVALID_ERROR,
  canonicalMoneyOrNull,
  moneyPreviewLabel,
  parseMoneyToCents,
} from "@/lib/contractMoney";
import {
  requiredTitleError,
  dateOrderError,
  errorForStep,
  createContractSubmitController,
} from "@/lib/newContractWizard";

const CONTRACT_TYPES = [
  ["installation", "Instalación"],
  ["public_tender", "Licitación pública"],
  ["framework", "Marco"],
  ["maintenance", "Mantenimiento"],
  ["mixed", "Mixto"],
  ["internal", "Interno"],
  ["other", "Otro"],
] as const;

const BILLING_STRATEGIES = [
  ["milestones", "Por hitos"],
  ["periodic_certifications", "Por certificaciones periódicas"],
  ["mixed", "Mixta: hitos y certificaciones"],
  ["single_invoice", "Factura única"],
  ["on_demand", "Puntual / bajo demanda"],
  ["non_billable", "No facturable"],
] as const;

const BILLING_REQUIREMENT_MODES = [
  ["milestones_required", "Facturación requerida"],
  ["optional_billable", "Facturable opcional"],
  ["account_current_required", "Cuenta corriente requerida"],
  ["no_billing_required", "Sin facturación"],
] as const;

const BILLING_CHANNELS = [
  ["email", "Email"],
  ["face", "FACe"],
  ["portal", "Portal"],
  ["other", "Otro"],
] as const;

const CONTRACT_DOCUMENT_STATUSES = [
  ["contract_pending", "Contrato pendiente"],
  ["contract_signed", "Contrato firmado"],
  ["contract_not_required", "Contrato no requerido"],
  ["contract_unknown", "Desconocido"],
] as const;

const PAYMENT_METHODS = [
  ["transfer", "Transferencia"],
  ["confirming", "Confirming"],
  ["promissory_note", "Pagaré"],
  ["other", "Otro"],
  ["cash", "Efectivo"],
] as const;

const PAYMENT_TERMS = [
  ["invoice_date", "Fecha de factura"],
  ["end_of_month", "Fin de mes"],
  ["client_acceptance", "Aceptación del cliente"],
  ["other", "Otro"],
] as const;

interface Props {
  customerPartnerId: number;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  canWriteEconomic?: boolean;
}

export function NewContractDialog(props: Props) {
  // Keep wizard state and its submit controller scoped to one customer. The
  // customer detail route can reuse its modal while navigating A → B.
  return <NewContractDialogForCustomer key={props.customerPartnerId} {...props} />;
}

function NewContractDialogForCustomer({
  customerPartnerId,
  open,
  onOpenChange,
  canWriteEconomic = false,
}: Props) {
  const [, setLocation] = useLocation();
  const utils = trpc.useUtils();
  const capabilities = trpc.customers.contracts.getMyCapabilities.useQuery(undefined, { enabled: open });
  const canLinkInstallations = capabilities.data?.canLinkInstallations === true;
  const canManageParties = capabilities.data?.canManageParties === true;
  const [continueWithInstallation, setContinueWithInstallation] = useState(false);
  const [billToPartner, setBillToPartner] = useState<BillToPartnerOption | null>(null);
  const billToPartnerRef = useRef<BillToPartnerOption | null>(null);
  billToPartnerRef.current = canManageParties ? billToPartner : null;
  const canManagePartiesRef = useRef(canManageParties);
  canManagePartiesRef.current = canManageParties;
  const billToFollowupFailedRef = useRef(false);

  const salesOwnersQuery = trpc.customers.listAssignableSalesOwners.useQuery(
    undefined,
    {
      enabled: open,
    }
  );
  const salesOwners = (salesOwnersQuery.data ?? []) as Array<{
    id: number;
    name: string;
    email: string;
  }>;

  const pmQuery =
    trpc.customers.contracts.listAssignableProjectManagers.useQuery(undefined, {
      enabled: open,
    });
  const projectManagers = (pmQuery.data ?? []) as Array<{
    id: number;
    name: string;
    email: string;
  }>;

  const customer360Query = trpc.customers.get360.useQuery(
    { partnerId: customerPartnerId },
    { enabled: open }
  );
  const customerBillingEmail =
    customer360Query.data?.billingEmail?.trim() || null;

  const [step, setStep] = useState(1);
  const [guided, setGuided] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [step]);
  const guidedRef = useRef(false);
  guidedRef.current = guided;
  const isReview = step === 5;
  const showSection = (section: number) =>
    !isReview && (!guided || step === section);
  const [title, setTitle] = useState("");
  const [contractType, setContractType] = useState("installation");
  const [tenderReference, setTenderReference] = useState("");
  const [externalContractNumber, setExternalContractNumber] = useState("");
  const [salesOwnerUserId, setSalesOwnerUserId] = useState("");
  const [projectManagerUserId, setProjectManagerUserId] = useState("");
  const [originalContractAmount, setOriginalContractAmount] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [billingRequirementMode, setBillingRequirementMode] = useState("");
  const [billingStrategy, setBillingStrategy] = useState("");
  const [billingChannel, setBillingChannel] = useState("");
  const [contractDocumentStatus, setContractDocumentStatus] = useState("");
  const [billingChannelNotes, setBillingChannelNotes] = useState("");
  const [
    certificationAllocationPercentage,
    setCertificationAllocationPercentage,
  ] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentTermBasis, setPaymentTermBasis] = useState("");
  const [paymentTermDays, setPaymentTermDays] = useState("");
  const [invoiceNotes, setInvoiceNotes] = useState("");
  const [awardDate, setAwardDate] = useState("");
  const [signedDate, setSignedDate] = useState("");
  const [contractualStartDate, setContractualStartDate] = useState("");
  const [contractualEndDate, setContractualEndDate] = useState("");
  const [includesMaintenance, setIncludesMaintenance] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // error is scoped to a step; null means no error.
  const [error, setError] = useState<string | null>(null);
  const [errorStep, setErrorStep] = useState<number | null>(null);

  const submittingRef = useRef(false);

  const create = trpc.customers.contracts.create.useMutation();
  const replacePrimaryPartnerRole = trpc.customers.contracts.replacePrimaryPartnerRole.useMutation();
  type TCreatePayload = Parameters<typeof create.mutateAsync>[0];
  const submitController = useRef<ReturnType<
    typeof createContractSubmitController<TCreatePayload>
  > | null>(null);
  if (submitController.current === null) {
    submitController.current = createContractSubmitController<TCreatePayload>({
      create: async p => {
        const created = await create.mutateAsync(p);
        const selectedBillTo = billToPartnerRef.current;
        if (
          canManagePartiesRef.current &&
          selectedBillTo &&
          selectedBillTo.id !== customerPartnerId
        ) {
          try {
            const current = await utils.customers.contracts.get.fetch({ contractId: created.id });
            await replacePrimaryPartnerRole.mutateAsync({
              contractId: created.id,
              role: "bill_to",
              newBusinessPartnerId: selectedBillTo.id,
              expectedUpdatedAt: current.updatedAt,
            });
          } catch {
            // The contract already exists. Keep the wizard's single-create flow
            // successful and tell the operator how to finish in Partes.
            billToFollowupFailedRef.current = true;
          }
          try {
            await utils.customers.contracts.get.invalidate({ contractId: created.id });
          } catch {
            // The detail route loads the canonical record after navigation.
          }
        }
        return created;
      },
      invalidateList: () =>
        utils.customers.contracts.listForCustomer.invalidate({
          customerPartnerId,
        }),
      navigate: path => setLocation(path),
      onSuccess: created => {
        reset();
        onOpenChange(false);
        if (billToFollowupFailedRef.current) {
          toast.error(
            `El contrato ${created.contractCode} ya está creado, pero no se pudo guardar «Facturar a». No vuelvas a crear el contrato; puedes corregirlo en Partes.`,
          );
        } else {
          toast.success(`Contrato creado: ${created.contractCode}`);
        }
        billToFollowupFailedRef.current = false;
      },
      onError: (message, step) => setStepError(message, step),
    });
  }
  useEffect(() => {
    if (open) {
      setContinueWithInstallation(false);
      setBillToPartner(null);
      billToFollowupFailedRef.current = false;
      setStep(1);
      clearError();
    }
  }, [open]);

  function clearError() {
    setError(null);
    setErrorStep(null);
  }

  function setStepError(message: string, targetStep: number) {
    setError(message);
    const visibleStep = guidedRef.current ? targetStep : 1;
    setErrorStep(visibleStep);
    setStep(visibleStep);
  }

  function reset() {
    setContinueWithInstallation(false);
    setBillToPartner(null);
    setTitle("");
    setContractType("installation");
    setTenderReference("");
    setExternalContractNumber("");
    setSalesOwnerUserId("");
    setProjectManagerUserId("");
    setOriginalContractAmount("");
    setCurrency("EUR");
    setBillingRequirementMode("");
    setBillingStrategy("");
    setBillingChannel("");
    setContractDocumentStatus("");
    setBillingChannelNotes("");
    setCertificationAllocationPercentage("");
    setPaymentMethod("");
    setPaymentTermBasis("");
    setPaymentTermDays("");
    setInvoiceNotes("");
    setAwardDate("");
    setSignedDate("");
    setContractualStartDate("");
    setContractualEndDate("");
    setIncludesMaintenance(false);
  }

  function handleNext() {
    const t = requiredTitleError(title);
    if (t) return setStepError(t, 1);
    if ((!guided || step === 3 || step === 4) && !validate()) return;
    clearError();
    setStep(guided ? step + 1 : 5);
  }

  function handleBack() {
    clearError();
    setStep(s => (guided ? Math.max(1, s - 1) : 1));
  }

  function handleCancel() {
    clearError();
    onOpenChange(false);
  }

  function validate() {
    const titleError = requiredTitleError(title);
    if (titleError) {
      setStepError(titleError, 1);
      return false;
    }
    const dateErr = dateOrderError(contractualStartDate, contractualEndDate);
    if (dateErr) {
      setStepError(dateErr, 4);
      return false;
    }
    const isBillable =
      billingRequirementMode !== "no_billing_required" &&
      billingStrategy !== "non_billable" &&
      (billingRequirementMode !== "" || billingStrategy !== "");
    if (canWriteEconomic && isBillable && !billingChannel) {
      setStepError(
        "Selecciona un canal de facturación para este contrato facturable.",
        3
      );
      return false;
    }
    if (
      canWriteEconomic &&
      billingChannel === "email" &&
      !customerBillingEmail
    ) {
      setStepError(
        "El destinatario de facturación no tiene email de facturación; completa ese dato antes de elegir Email.",
        3
      );
      return false;
    }
    if (
      canWriteEconomic &&
      originalContractAmount.trim() !== "" &&
      parseMoneyToCents(originalContractAmount) === null
    ) {
      setStepError(MONEY_INVALID_ERROR, 3);
      return false;
    }
    if (
      canWriteEconomic &&
      billingStrategy === "mixed" &&
      certificationAllocationPercentage !== "" &&
      (!Number.isFinite(Number(certificationAllocationPercentage)) ||
        Number(certificationAllocationPercentage) < 0 ||
        Number(certificationAllocationPercentage) > 100)
    ) {
      setStepError(
        "El porcentaje de certificaciones debe estar entre 0 y 100.",
        3
      );
      return false;
    }
    if (
      canWriteEconomic &&
      paymentTermDays !== "" &&
      (!Number.isInteger(Number(paymentTermDays)) ||
        Number(paymentTermDays) < 0 ||
        Number(paymentTermDays) > 365)
    ) {
      setStepError("El plazo debe ser un número entero entre 0 y 365 días.", 3);
      return false;
    }
    return true;
  }

  async function handleSubmit() {
    if (!isReview || submittingRef.current || !validate()) return;
    submittingRef.current = true;
    setSubmitting(true);
    clearError();
    const payload: TCreatePayload = {
      customerPartnerId,
      title: title.trim(),
      contractType: contractType as TCreatePayload["contractType"],
      tenderReference: tenderReference.trim() || null,
      externalContractNumber: externalContractNumber.trim() || null,
      awardDate: awardDate || null,
      signedDate: signedDate || null,
      contractualStartDate: contractualStartDate || null,
      contractualEndDate: contractualEndDate || null,
      salesOwnerUserId: salesOwnerUserId ? Number(salesOwnerUserId) : null,
      projectManagerUserId: projectManagerUserId
        ? Number(projectManagerUserId)
        : null,
      includesMaintenance,
    };
    if (canWriteEconomic) {
      if (originalContractAmount.trim() !== "")
        payload.originalContractAmount = canonicalMoneyOrNull(
          originalContractAmount
        );
      payload.currency = currency;
      payload.billingStrategy = (billingStrategy ||
        null) as TCreatePayload["billingStrategy"];
      if (billingStrategy === "mixed" && certificationAllocationPercentage) {
        payload.certificationAllocationPercentage = Number(
          certificationAllocationPercentage
        );
      }
      payload.billingRequirementMode = (billingRequirementMode ||
        null) as TCreatePayload["billingRequirementMode"];
      payload.billingChannel = (billingChannel ||
        null) as TCreatePayload["billingChannel"];
      payload.contractDocumentStatus = (contractDocumentStatus || null) as TCreatePayload["contractDocumentStatus"];
      payload.billingChannelNotes = billingChannelNotes.trim() || null;
      payload.paymentMethod = (paymentMethod ||
        null) as TCreatePayload["paymentMethod"];
      payload.paymentTermBasis = (paymentTermBasis ||
        null) as TCreatePayload["paymentTermBasis"];
      payload.paymentTermDays =
        paymentTermDays !== "" ? Number(paymentTermDays) : null;
      payload.invoiceNotes = invoiceNotes.trim() || null;
    }
    try {
      const controller = submitController.current!;
      await controller.run(payload, { addInstallation: continueWithInstallation && canLinkInstallations });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  const display = (value: string | undefined | null) =>
    value?.trim() || "No informado";
  const label = (
    options: readonly (readonly [string, string])[],
    value: string
  ) => options.find(([key]) => key === value)?.[1] ?? "No informado";
  const person = (people: typeof salesOwners, id: string) =>
    id
      ? people.find(p => String(p.id) === id)?.name || "Nombre no disponible"
      : "No informado";
  const economic = (value: string) =>
    canWriteEconomic ? value : "Sin permiso económico";
  const date = (value: string) =>
    value ? value.split("-").reverse().join("/") : "No informado";
  const cents = parseMoneyToCents(originalContractAmount);
  const summary: Array<[string, string, number]> = [
    ["Parte contratante", display(customer360Query.data?.legalName), 1],
    [
      "Facturar a",
      canManageParties && billToPartner
        ? `[${billToPartner.partnerCode}] ${billToPartner.legalName}${billToPartner.id === customerPartnerId ? " · igual a la Parte contratante" : " · distinta de la Parte contratante"}`
        : `Igual que la Parte contratante · ${display(customer360Query.data?.legalName)} (predeterminado)`,
      1,
    ],
    ["Contrato", display(title), 1],
    ["Tipo", label(CONTRACT_TYPES, contractType), 1],
    ["Referencia de licitación", display(tenderReference), 1],
    ["Número contractual externo", display(externalContractNumber), 1],
    ["Responsable comercial", person(salesOwners, salesOwnerUserId), 2],
    ["Project Manager", person(projectManagers, projectManagerUserId), 2],
    [
      "Importe contractual SIN IVA",
      economic(
        cents === null
          ? "No informado"
          : `${formatCentsAsEs(cents)} ${currency}`
      ),
      3,
    ],
    ["Estado del documento contractual", economic(label(CONTRACT_DOCUMENT_STATUSES, contractDocumentStatus)), 3],
    [
      "Modo de facturación",
      economic(label(BILLING_REQUIREMENT_MODES, billingRequirementMode)),
      3,
    ],
    ["Estrategia", economic(label(BILLING_STRATEGIES, billingStrategy)), 3],
    ...(billingStrategy === "mixed"
      ? [
          [
            "Reparto",
            economic(
              certificationAllocationPercentage === ""
                ? "No informado"
                : `${certificationAllocationPercentage}% certificaciones · ${100 - Number(certificationAllocationPercentage)}% hitos`
            ),
            3,
          ] as [string, string, number],
        ]
      : []),
    [
      "Canal de facturación",
      economic(label(BILLING_CHANNELS, billingChannel)),
      3,
    ],
    ...(billingChannel === "email"
      ? [
          [
            "Email de facturación",
            economic(display(customerBillingEmail)),
            3,
          ] as [string, string, number],
        ]
      : []),
    ["Notas del canal", economic(display(billingChannelNotes)), 3],
    ["Método de pago", economic(label(PAYMENT_METHODS, paymentMethod)), 3],
    ["Base de plazo", economic(label(PAYMENT_TERMS, paymentTermBasis)), 3],
    ["Días de plazo", economic(display(paymentTermDays)), 3],
    ["Notas de factura", economic(display(invoiceNotes)), 3],
    ["Adjudicación", date(awardDate), 4],
    ["Firma", date(signedDate), 4],
    ["Inicio contractual", date(contractualStartDate), 4],
    ["Fin contractual", date(contractualEndDate), 4],
    ["Incluye mantenimiento", includesMaintenance ? "Sí" : "No", 4],
  ];

  const currentError = errorForStep(error, errorStep, step);

  return (
    <Dialog
      open={open}
      onOpenChange={value => {
        if (!submittingRef.current) onOpenChange(value);
      }}
    >
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-xl overflow-hidden flex flex-col sm:max-w-2xl [&_[data-slot=select-trigger]]:h-auto [&_[data-slot=select-trigger]]:min-h-10 [&_[data-slot=select-trigger]]:whitespace-normal [&_[data-slot=select-trigger]]:w-full [&_[data-slot=select-value]]:line-clamp-none [&_[data-slot=select-value]]:min-w-0">
        <DialogHeader className="shrink-0 min-w-0 pr-6">
          <DialogTitle>
            Nuevo contrato · {isReview ? "Resumen" : "Datos"} · paso{" "}
            {guided ? step : isReview ? 2 : 1} de {guided ? 5 : 2}
          </DialogTitle>
          <DialogDescription>
            Primero crea el contrato. Después podrás añadir una o varias
            instalaciones desde su ficha, sin repetir el importe contractual.
          </DialogDescription>
        </DialogHeader>

        <div
          ref={scrollRef}
          className="min-h-0 min-w-0 overflow-y-auto space-y-5 pr-1 [overflow-wrap:anywhere]"
        >
          {!isReview && (
            <Button
              type="button"
              variant="outline"
              className="min-h-11 h-auto whitespace-normal"
              onClick={() => {
                setGuided(!guided);
                setStep(1);
                clearError();
              }}
            >
              {guided ? "Usar formulario breve" : "Completar por secciones"}
            </Button>
          )}
          {isReview && (
            <section aria-label="Resumen del contrato" className="space-y-4">
              <p className="text-sm">
                Comprueba los datos. No se guardará nada hasta confirmar el contrato.
              </p>
              {canLinkInstallations && (
                <div className="rounded-md border p-3 space-y-2">
                  <label className="flex items-start gap-3 py-2 text-sm">
                    <input type="checkbox" className="mt-1 h-5 w-5 shrink-0" checked={continueWithInstallation}
                      disabled={submitting} onChange={e => setContinueWithInstallation(e.target.checked)} />
                    Añadir la primera instalación después de crear el contrato
                  </label>
                  <p className="text-sm text-muted-foreground">{continueWithInstallation
                    ? "Al confirmar, se crea el contrato y podrás añadir la primera instalación con la misma referencia económica. Si cancelas el alta de la instalación, el contrato se conserva."
                        : "Sin marcar, se crea solo el contrato. Podrás añadir la primera instalación más adelante sin volver a crear el contrato."}</p>
                </div>
              )}
              <dl className="space-y-3">
                {summary.map(([name, value, section]) => (
                  <div
                    key={name}
                    className="grid min-w-0 gap-1 sm:grid-cols-[1fr_1fr_auto]"
                  >
                    <dt className="text-sm text-muted-foreground">{name}</dt>
                    <dd className="min-w-0 whitespace-pre-wrap text-sm font-medium">
                      {value}
                    </dd>
                    {name !== "Parte contratante" && name !== "Email de facturación" && (name !== "Facturar a" || canManageParties) && (section !== 3 || canWriteEconomic) && <dd>
                      <Button
                        type="button"
                        variant="ghost"
                        disabled={submitting}
                        className="min-h-11"
                        aria-label={`Editar ${name}`}
                        onClick={() => {
                          clearError();
                          setStep(guided ? section : 1);
                        }}
                      >
                        Editar
                      </Button>
                    </dd>}
                  </div>
                ))}
              </dl>
            </section>
          )}
          {showSection(1) && (
            <div className="space-y-4">
              <div className="min-w-0 rounded-md border p-3 [overflow-wrap:anywhere]">
                <p className="text-sm font-medium">Parte contratante</p>
                <p className="text-sm text-muted-foreground">{display(customer360Query.data?.legalName)}</p>
              </div>
              {canManageParties && (
                <BillToPartnerPicker
                  value={billToPartner}
                  onChange={setBillToPartner}
                  allowContractingPartyDefault
                  disabled={submitting}
                />
              )}
              <div className="space-y-1.5">
                <Label htmlFor="ct-title">Título *</Label>
                <Input
                  id="ct-title"
                  value={title}
                  onChange={e => {
                    setTitle(e.target.value);
                    if (errorStep === 1) clearError();
                  }}
                  placeholder="Ej. Instalación fotovoltaica 100 kW"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Tipo</Label>
                <Select value={contractType} onValueChange={setContractType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-w-[calc(100vw-3rem)] [overflow-wrap:anywhere]">
                    {CONTRACT_TYPES.map(([v, l]) => (
                      <SelectItem key={v} value={v}>
                        {l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <details open={guided || undefined} className="space-y-4">
                <summary className="cursor-pointer py-3 text-sm">
                  Referencias adicionales (opcional)
                </summary>
                <div className="space-y-1.5">
                  <Label htmlFor="ct-tender">Referencia de licitación</Label>
                  <Input
                    id="ct-tender"
                    value={tenderReference}
                    onChange={e => setTenderReference(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ct-ext">Número contractual externo</Label>
                  <Input
                    id="ct-ext"
                    value={externalContractNumber}
                    onChange={e => setExternalContractNumber(e.target.value)}
                  />
                </div>
              </details>
            </div>
          )}

          {showSection(2) && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Responsable comercial</Label>
                <Select
                  value={salesOwnerUserId}
                  onValueChange={v => {
                    setSalesOwnerUserId(v);
                    if (errorStep === 2) clearError();
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona comercial" />
                  </SelectTrigger>
                  <SelectContent className="max-w-[calc(100vw-3rem)] [overflow-wrap:anywhere]">
                    {salesOwners.map(u => (
                      <SelectItem key={u.id} value={String(u.id)}>
                        {u.name}
                        {u.email ? ` · ${u.email}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Project Manager</Label>
                <Select
                  value={projectManagerUserId}
                  onValueChange={v => {
                    setProjectManagerUserId(v);
                    if (errorStep === 2) clearError();
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona PM" />
                  </SelectTrigger>
                  <SelectContent className="max-w-[calc(100vw-3rem)] [overflow-wrap:anywhere]">
                    {projectManagers.map(u => (
                      <SelectItem key={u.id} value={String(u.id)}>
                        {u.name}
                        {u.email ? ` · ${u.email}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {showSection(3) &&
            (canWriteEconomic ? (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="ct-amount">Importe contractual sin IVA</Label>
                  <Input
                    id="ct-amount"
                    type="text"
                    inputMode="decimal"
                    value={originalContractAmount}
                    onChange={e => setOriginalContractAmount(e.target.value)}
                    placeholder="Ej. 28.228,25"
                  />
                  {(() => {
                    const trimmed = originalContractAmount.trim();
                    if (trimmed === "") return null;
                    const cents = parseMoneyToCents(trimmed);
                    return cents === null ? (
                      <p className="text-xs text-destructive">
                        {MONEY_INVALID_ERROR}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {moneyPreviewLabel(cents, currency)}
                      </p>
                    );
                  })()}
                  <p className="text-xs text-muted-foreground">
                    Base imponible adjudicada. Introducir siempre el importe sin
                    IVA.
                  </p>
                </div>
                <details open={guided || undefined} className="space-y-4">
                  <summary className="cursor-pointer py-3 text-sm">
                    Moneda (EUR por defecto)
                  </summary>
                  <div className="space-y-1.5">
                    <Label htmlFor="ct-currency">Moneda</Label>
                    <Input
                      id="ct-currency"
                      value={currency}
                      maxLength={3}
                      onChange={e => setCurrency(e.target.value.toUpperCase())}
                    />
                  </div>
                </details>
                <div className="space-y-1.5">
                  <Label>Estado del documento contractual</Label>
                  <Select value={contractDocumentStatus} onValueChange={setContractDocumentStatus}>
                    <SelectTrigger><SelectValue placeholder="Selecciona estado (opcional)" /></SelectTrigger>
                    <SelectContent className="max-w-[calc(100vw-3rem)] [overflow-wrap:anywhere]">
                      {CONTRACT_DOCUMENT_STATUSES.map(([v, l]) => (
                        <SelectItem key={v} value={v}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Modo de facturación</Label>
                  <Select
                    value={billingRequirementMode}
                    onValueChange={v => {
                      setBillingRequirementMode(v);
                      if (errorStep === 3) clearError();
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona modo" />
                    </SelectTrigger>
                    <SelectContent className="max-w-[calc(100vw-3rem)] [overflow-wrap:anywhere]">
                      {BILLING_REQUIREMENT_MODES.map(([v, l]) => (
                        <SelectItem key={v} value={v}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Estrategia de facturación</Label>
                  <Select
                    value={billingStrategy}
                    onValueChange={v => {
                      setBillingStrategy(v);
                      if (errorStep === 3) clearError();
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona estrategia" />
                    </SelectTrigger>
                    <SelectContent className="max-w-[calc(100vw-3rem)] [overflow-wrap:anywhere]">
                      {BILLING_STRATEGIES.map(([v, l]) => (
                        <SelectItem key={v} value={v}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>
                    Canal de facturación
                    {billingRequirementMode !== "no_billing_required" &&
                    billingStrategy !== "non_billable" &&
                    (billingRequirementMode || billingStrategy)
                      ? " *"
                      : ""}
                  </Label>
                  <Select
                    value={billingChannel}
                    onValueChange={v => {
                      setBillingChannel(v);
                      if (errorStep === 3) clearError();
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona canal" />
                    </SelectTrigger>
                    <SelectContent className="max-w-[calc(100vw-3rem)] [overflow-wrap:anywhere]">
                      {BILLING_CHANNELS.map(([v, l]) => (
                        <SelectItem
                          key={v}
                          value={v}
                          disabled={v === "email" && !customerBillingEmail}
                        >
                          {l}
                          {v === "email" && !customerBillingEmail
                            ? " · destinatario de facturación sin email"
                            : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!customerBillingEmail && (
                    <p className="text-xs text-amber-700">
                      Email no disponible: completa el email de facturación del
                      destinatario de facturación para poder usar este canal.
                    </p>
                  )}
                  {billingChannel === "portal" || billingChannel === "other" ? (
                    <Textarea
                      value={billingChannelNotes}
                      onChange={e => setBillingChannelNotes(e.target.value)}
                      placeholder={
                        billingChannel === "portal"
                          ? "Referencia de plataforma"
                          : "Notas del canal"
                      }
                      maxLength={2000}
                    />
                  ) : null}
                </div>

                {billingStrategy === "mixed" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="ct-cert-pct">
                      % reservado para certificaciones (0–100)
                    </Label>
                    <Input
                      id="ct-cert-pct"
                      type="number"
                      min={0}
                      max={100}
                      value={certificationAllocationPercentage}
                      onChange={e => {
                        setCertificationAllocationPercentage(e.target.value);
                        if (errorStep === 3) clearError();
                      }}
                      placeholder="Ej. 70"
                    />
                    {certificationAllocationPercentage && (
                      <p className="text-xs text-muted-foreground">
                        Hitos cubrirán el{" "}
                        {100 - Number(certificationAllocationPercentage)}%
                        restante.
                      </p>
                    )}
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>Método de pago</Label>
                  <Select
                    value={paymentMethod}
                    onValueChange={setPaymentMethod}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona método" />
                    </SelectTrigger>
                    <SelectContent className="max-w-[calc(100vw-3rem)] [overflow-wrap:anywhere]">
                      {PAYMENT_METHODS.map(([v, l]) => (
                        <SelectItem key={v} value={v}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Base de plazo</Label>
                  <Select
                    value={paymentTermBasis}
                    onValueChange={setPaymentTermBasis}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona base" />
                    </SelectTrigger>
                    <SelectContent className="max-w-[calc(100vw-3rem)] [overflow-wrap:anywhere]">
                      {PAYMENT_TERMS.map(([v, l]) => (
                        <SelectItem key={v} value={v}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ct-days">Días de plazo</Label>
                  <Input
                    id="ct-days"
                    type="number"
                    min="0"
                    max="365"
                    value={paymentTermDays}
                    onChange={e => setPaymentTermDays(e.target.value)}
                  />
                </div>
                <details open={guided || undefined} className="space-y-4">
                  <summary className="cursor-pointer py-3 text-sm">
                    Notas de factura (opcional)
                  </summary>
                  <div className="space-y-1.5">
                    <Label htmlFor="ct-notes">Notas de factura</Label>
                    <Textarea
                      id="ct-notes"
                      value={invoiceNotes}
                      onChange={e => setInvoiceNotes(e.target.value)}
                    />
                  </div>
                </details>
              </div>
            ) : (
              <div className="py-10 text-center text-sm text-muted-foreground">
                Los datos económicos se definirán más adelante por un rol con
                permiso de economía.
              </div>
            ))}

          {showSection(4) && (
            <details
              open={guided || errorStep === step || undefined}
              className="space-y-4"
            >
              <summary className="cursor-pointer py-3 text-sm">
                Compromiso contractual (opcional)
              </summary>
              <div className="space-y-1.5">
                <Label htmlFor="ct-award">Fecha de adjudicación</Label>
                <Input
                  id="ct-award"
                  type="date"
                  value={awardDate}
                  onChange={e => setAwardDate(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ct-signed">Fecha de firma</Label>
                <Input
                  id="ct-signed"
                  type="date"
                  value={signedDate}
                  onChange={e => setSignedDate(e.target.value)}
                />
              </div>
              <div className="space-y-3 rounded-md border p-3">
                <p className="text-xs text-muted-foreground">Estas fechas representan el plazo contractual del contrato. La planificación de cada instalación/fase se gestiona por separado.</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="ct-start">Inicio contractual</Label>
                    <Input id="ct-start" type="date" value={contractualStartDate} onChange={e => { setContractualStartDate(e.target.value); if (errorStep === 4) clearError(); }} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ct-end">Fin contractual</Label>
                    <Input id="ct-end" type="date" value={contractualEndDate} onChange={e => { setContractualEndDate(e.target.value); if (errorStep === 4) clearError(); }} />
                  </div>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={includesMaintenance}
                  onChange={e => setIncludesMaintenance(e.target.checked)}
                />
                Incluye mantenimiento
              </label>
            </details>
          )}

          {currentError && (
            <p role="alert" className="text-sm text-destructive">
              {currentError}
            </p>
          )}
        </div>

        <DialogFooter className="shrink-0 flex-col-reverse gap-2 sm:flex-row [&_button]:min-h-11">
          <Button
            type="button"
            variant="outline"
            disabled={submitting}
            onClick={step > 1 ? handleBack : handleCancel}
          >
            {step > 1 ? "Atrás" : "Cancelar"}
          </Button>
          {!isReview ? (
            <Button type="button" disabled={submitting} onClick={handleNext}>
              {!guided || step === 4 ? "Revisar contrato" : "Siguiente"}
            </Button>
          ) : (
            <Button type="button" className="h-auto whitespace-normal" disabled={submitting} onClick={handleSubmit}>
              {submitting ? "Creando..." : continueWithInstallation && canLinkInstallations ? "Confirmar contrato y añadir instalación" : "Confirmar y crear contrato"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
